import { Box } from "@mui/material";
import Alert, { type AlertColor } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

type Notification = {
	id: number;
	message: string;
	severity: AlertColor; // 'success' | 'error' | 'warning' | 'info'
};

type NotificationContextType = {
	showNotification: (message: string, severity: AlertColor) => void;
};

const MAX_VISIBLE_NOTIFICATIONS = 5; // Maximum number of notifications visible at the same time

const NotificationContext = createContext<NotificationContextType | undefined>(
	undefined,
);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [queue, setQueue] = useState<Notification[]>([]); // Queue to store all notifications
	const [visibleNotifications, setVisibleNotifications] = useState<
		Notification[]
	>([]); // Currently visible notifications

	// Adds a new notification to the queue
	const showNotification = useCallback((message: string, severity: AlertColor): void => {
		const id = Date.now() + Math.random(); // Unique ID based on timestamp and random number
		setQueue((prevQueue) => [...prevQueue, { id, message, severity }]);
	}, []);

	// Processes the notification queue whenever there is space for a new notification
	useEffect(() => {
		if (
			visibleNotifications.length < MAX_VISIBLE_NOTIFICATIONS &&
			queue.length > 0
		) {
			const nextNotification = queue[0]; // Get the next notification from the queue
			if (!nextNotification) return;

			setQueue((prevQueue) => prevQueue.slice(1)); // Remove it from the queue
			setVisibleNotifications((prevVisible) => [
				...prevVisible,
				nextNotification,
			]);

			// Automatically remove the notification after 3 seconds
			const notificationId = nextNotification.id; // Capture ID for closure
			setTimeout(() => {
				setVisibleNotifications((prevVisible) =>
					prevVisible.filter(
						(notification) => notification.id !== notificationId,
					),
				);
			}, 3000);
		}
	}, [queue, visibleNotifications]);

	// Memoize the context value to prevent unnecessary re-renders of consumers
	const contextValue = useMemo(
		() => ({ showNotification }),
		[showNotification],
	);

	return (
		<NotificationContext.Provider value={contextValue}>
			{children}
			<Box
				sx={{
					position: "fixed",
					top: 26,
					right: 36,
					display: "flex",
					flexDirection: "column",
					gap: 1,
					zIndex: 1400,
				}}
			>
				{visibleNotifications.map((notification) => (
					<Snackbar
						key={notification.id}
						open
						sx={{ position: "relative" }}
					>
						<Alert severity={notification.severity} sx={{ width: "100%" }}>
							{notification.message}
						</Alert>
					</Snackbar>
				))}
			</Box>
		</NotificationContext.Provider>
	);
};

export const useNotification = (): NotificationContextType => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error(
			"useNotification must be used within a NotificationProvider",
		);
	}
	return context;
};
